import React, { Fragment, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auditsApi, auditorsApi, certificatesApi, auditItemsApi, sacpmApi } from '../api';
import AuditItems from './AuditItems';
import Auditors from './Auditors';
import ExportButtons from '../components/ExportButtons';
import ColumnSelector from '../components/ColumnSelector';
import useColumnVisibility from '../hooks/useColumnVisibility';
import { toast } from 'sonner';
import { validateDateFields, extractApiError } from '../utils/dateValidation';
import {
  RefreshCw,
  Plus,
  Search,
  Edit,
  Trash2,
  X,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  Eye,
  FileText,
  Calendar,
  Users,
  ClipboardCheck,
  UserCheck,
  AlertTriangle,
  CheckSquare,
  Download,
} from 'lucide-react';

const AUDIT_TYPES = [
  { value: 'internal', label: 'Interna' },
  { value: 'external', label: 'Externa' },
  { value: 'supplier', label: 'Fornecedor' },
  { value: 'certification', label: 'Certificação' },
  { value: 'surveillance', label: 'Manutenção' },
  { value: 'process', label: 'Processo' },
];

const CHECKLIST_RESULTS = [
  { value: 'conforming', label: 'Conforme', active: 'text-green-400 border-green-500/40' },
  { value: 'non_conforming', label: 'Não Conforme', active: 'text-red-400 border-red-500/40' },
  { value: 'not_applicable', label: 'N/A', active: 'text-gray-400 border-gray-500/40' },
  { value: 'not_verified', label: 'Não Verificado', active: 'text-yellow-400 border-yellow-500/40' },
];

const STATUS_CONFIG = {
  planned: { label: 'Planejada', color: 'badge-gray', icon: Calendar },
  scheduled: { label: 'Agendada', color: 'badge-blue', icon: Clock },
  in_progress: { label: 'Em Andamento', color: 'badge-yellow', icon: RefreshCw },
  completed: { label: 'Concluída', color: 'badge-green', icon: CheckCircle },
  cancelled: { label: 'Cancelada', color: 'badge-red', icon: XCircle },
};

const RESULT_CONFIG = {
  approved: { label: 'Aprovado', color: 'badge-green' },
  approved_restrictions: { label: 'Aprovado com Restrições', color: 'badge-yellow' },
  disapproved: { label: 'Reprovado', color: 'badge-red' },
  pending: { label: 'Pendente', color: 'badge-gray' },
};

const Audits = () => {
  const [activeTab, setActiveTab] = useState('audits');
  const navigate = useNavigate();
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editingAudit, setEditingAudit] = useState(null);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [detailTab, setDetailTab] = useState('info');
  const [checklistItems, setChecklistItems] = useState([]);
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState({ question: '', requirement: '', area: '' });
  const [reportForm, setReportForm] = useState({ summary: '', conclusions: '', recommendations: '' });
  const [savingReport, setSavingReport] = useState(false);
  const [auditors, setAuditors] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [certItemCount, setCertItemCount] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    audit_type: 'internal',
    scope: '',
    objectives: '',
    criteria: '',
    areas: '',
    planned_date: '',
    start_date: '',
    end_date: '',
    auditors: '',
    share_email: '',
    certificate_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [pendingNcItem, setPendingNcItem] = useState(null);
  const [creatingSacpm, setCreatingSacpm] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);

  useEffect(() => {
    loadData();
    loadAuditors();
    loadCertificates();
  }, [filterType, filterStatus]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterType) params.audit_type = filterType;
      if (filterStatus) params.status = filterStatus;
      
      const [auditsRes, statsRes] = await Promise.all([
        auditsApi.list(params),
        auditsApi.stats(),
      ]);
      setAudits(auditsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditors = async () => {
    try {
      const res = await auditorsApi.listActive();
      setAuditors(res.data || []);
    } catch (error) {
      console.error('Error loading auditors:', error);
    }
  };

  const loadCertificates = async () => {
    try {
      const res = await certificatesApi.listActive();
      setCertificates(res.data || []);
    } catch (error) {
      console.error('Error loading certificates:', error);
    }
  };

  const handleToggleCertificate = (id) => {
    setFormData((prev) => {
      const has = prev.certificate_ids.includes(id);
      const next = has
        ? prev.certificate_ids.filter((c) => c !== id)
        : [...prev.certificate_ids, id];
      previewChecklistItems(next);
      return { ...prev, certificate_ids: next };
    });
  };

  const handleToggleAuditor = (id) => {
    setFormData((prev) => {
      const has = prev.selected_auditor_ids.includes(id);
      const next = has
        ? prev.selected_auditor_ids.filter((a) => a !== id)
        : [...prev.selected_auditor_ids, id];
      const names = auditors.filter(a => next.includes(a.id)).map(a => a.name);
      return { ...prev, selected_auditor_ids: next, auditors: names.join(', ') };
    });
  };

  const previewChecklistItems = async (certIds) => {
    if (!certIds.length) {
      setCertItemCount(null);
      return;
    }
    try {
      const res = await auditItemsApi.list({ certificate_ids: certIds.join(','), limit: 500 });
      setCertItemCount(res.data?.length ?? null);
    } catch (error) {
      setCertItemCount(null);
    }
  };

  const handleOpenModal = async (audit = null) => {
    if (audit) {
      try {
        const res = await auditsApi.get(audit.id);
        const fullAudit = res.data;
        const selectedNames = (fullAudit.auditors || '').split(',').map(s => s.trim()).filter(Boolean);
        const selectedAuditorIds = auditors.filter(a => selectedNames.includes(a.name)).map(a => a.id);
        setEditingAudit(fullAudit);
        setFormData({
          title: fullAudit.title,
          description: fullAudit.description || '',
          audit_type: fullAudit.audit_type,
          scope: fullAudit.scope || '',
          objectives: fullAudit.objectives || '',
          criteria: fullAudit.criteria || '',
          areas: fullAudit.areas || '',
          planned_date: fullAudit.planned_date || '',
          start_date: fullAudit.start_date || '',
          end_date: fullAudit.end_date || '',
          auditors: fullAudit.auditors || '',
          share_email: fullAudit.share_email || '',
          selected_auditor_ids: selectedAuditorIds,
        });
        setCertItemCount(null);
        setPendingDocuments([]);
        setDocuments([]);
        setShowModal(true);
      } catch (error) {
        toast.error('Erro ao carregar dados da auditoria');
      }
    } else {
      setEditingAudit(null);
      setFormData({
        title: '',
        description: '',
        audit_type: 'internal',
        scope: '',
        objectives: '',
        criteria: '',
        areas: '',
        planned_date: '',
        start_date: '',
        end_date: '',
        auditors: '',
        share_email: '',
        certificate_ids: [],
        selected_auditor_ids: [],
      });
      setCertItemCount(null);
      setPendingDocuments([]);
      setDocuments([]);
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAudit(null);
    setPendingDocuments([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title) {
      toast.error('Preencha o título');
      return;
    }

    const dateError = validateDateFields([
      { value: formData.planned_date, label: 'Data Planejada' },
      { value: formData.start_date, label: 'Data Início' },
      { value: formData.end_date, label: 'Data Fim' },
    ]);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    if (formData.start_date && formData.end_date && formData.end_date < formData.start_date) {
      toast.error('A data de fim não pode ser anterior à data de início');
      return;
    }
    
    const isExternal = formData.audit_type !== 'internal';
    const hasFiles = pendingDocuments.length > 0;

    if (!editingAudit && isExternal && hasFiles && !pendingDocuments.every(d => d.file)) {
      toast.error('Escolha os arquivos a anexar');
      return;
    }
    
    setSaving(true);
    
    try {
      if (editingAudit && isExternal && pendingDocuments.length) {
        await uploadFiles(pendingDocuments, editingAudit.id);
        setPendingDocuments([]);
      }
      const data = {
        ...formData,
        planned_date: formData.planned_date || null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        share_email: formData.share_email || null,
      };
      delete data.selected_auditor_ids;

      if (editingAudit) {
        delete data.certificate_ids;
        await auditsApi.update(editingAudit.id, data);
        toast.success('Auditoria atualizada!');
      } else {
        if (isExternal) {
          delete data.certificate_ids;
        } else {
          data.certificate_ids = formData.certificate_ids.length ? formData.certificate_ids : null;
        }
        const newAudit = await auditsApi.create(data);
        if (isExternal && hasFiles) {
          await uploadFiles(pendingDocuments, newAudit.data.id);
        }
        toast.success('Auditoria criada!');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  };

  const uploadFiles = async (fileList, auditId) => {
    for (const f of fileList) {
      if (f.file) {
        await auditsApi.uploadDocument(auditId, f.file);
      }
    }
  };

  const handleAddPendingFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingDocuments(prev => [...prev, ...files.map(file => ({ file }))]);
    e.target.value = '';
  };

  const handleRemovePendingFile = (index) => {
    setPendingDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const loadDocuments = async (auditId) => {
    try {
      setLoadingDocuments(true);
      const res = await auditsApi.listDocuments(auditId);
      setDocuments(res.data);
    } catch { toast.error('Erro ao carregar documentos'); } finally { setLoadingDocuments(false); }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await auditsApi.uploadDocument(selectedAudit.id, file);
      toast.success('Documento anexado!');
      loadDocuments(selectedAudit.id);
    } catch (err) { toast.error(extractApiError(err, 'Erro ao anexar documento')); }
    e.target.value = '';
  };

  const handleDownloadDocument = async (doc) => {
    try {
      const res = await auditsApi.downloadDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao baixar documento'); }
  };

  const handleDeleteDocument = async (doc) => {
    if (!window.confirm(`Excluir documento "${doc.file_name}"?`)) return;
    try {
      await auditsApi.deleteDocument(doc.id);
      toast.success('Documento excluído!');
      loadDocuments(selectedAudit.id);
    } catch { toast.error('Erro ao excluir documento'); }
  };

  const handleDelete = async (audit) => {
    if (!window.confirm(`Excluir auditoria "${audit.code}"?`)) return;
    
    try {
      await auditsApi.delete(audit.id);
      toast.success('Auditoria excluída!');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleFinalize = async (audit) => {
    if (!window.confirm(`Finalizar a auditoria "${audit.code}"?\n\nApós a finalização, a auditoria não poderá mais ser editada.`)) return;

    try {
      const today = new Date().toISOString().split('T')[0];
      await auditsApi.update(audit.id, {
        status: 'completed',
        end_date: audit.end_date || today,
      });
      toast.success('Auditoria finalizada!');
      loadData();
    } catch (error) {
      toast.error('Erro ao finalizar auditoria');
    }
  };

  const handleViewDetails = async (audit) => {
    try {
      const res = await auditsApi.get(audit.id);
      setSelectedAudit(res.data);
      setDetailTab('info');
      setReportForm({
        summary: res.data.summary || '',
        conclusions: res.data.conclusions || '',
        recommendations: res.data.recommendations || '',
      });
      setShowDetailModal(true);
      setChecklistItems([]);
      setDocuments([]);
      if (res.data.audit_type !== 'internal') {
        loadDocuments(audit.id);
      }
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  const loadChecklistItems = async (auditId) => {
    try {
      setLoadingChecklist(true);
      const res = await auditsApi.listChecklist(auditId);
      setChecklistItems(res.data);
    } catch { toast.error('Erro ao carregar checklist'); } finally { setLoadingChecklist(false); }
  };

  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.question.trim()) return;
    try {
      await auditsApi.addChecklistItem(selectedAudit.id, {
        ...newChecklistItem,
        sequence: checklistItems.length + 1,
      });
      setNewChecklistItem({ question: '', requirement: '', area: '' });
      loadChecklistItems(selectedAudit.id);
    } catch { toast.error('Erro ao adicionar item'); }
  };

  const handleUpdateChecklistResult = async (itemId, result) => {
    try {
      await auditsApi.updateChecklistItem(itemId, { result });
      setChecklistItems(prev => prev.map(i => i.id === itemId ? { ...i, result } : i));
      const current = checklistItems.find(i => i.id === itemId);
      if (result === 'non_conforming' && current?.result !== 'non_conforming') {
        setPendingNcItem(current || { id: itemId });
      }
    } catch { toast.error('Erro ao atualizar'); }
  };

  const handleCreateSacpmFromAudit = async (approved) => {
    const item = pendingNcItem;
    setPendingNcItem(null);
    if (!approved || !item || !selectedAudit) return;
    setCreatingSacpm(true);
    try {
      const requirement = item.requirement ? `Requisito: ${item.requirement}\n` : '';
      const area = item.area ? `Área: ${item.area}` : '';
      const description =
        `Item de auditoria não conforme (${selectedAudit.code})\n\n${requirement}Item: ${item.question}\n${area}`.trim();
      const title = `NC em Auditoria ${selectedAudit.code}: ${item.question}`.slice(0, 300);
      const res = await sacpmApi.create({
        title,
        description,
        problem_description: description,
        sacpm_type: 'corrective',
        origin: 'audit',
        area: item.area || null,
        audit_id: selectedAudit.id,
      });
      toast.success(`SACPM ${res.data.code} aberto a partir da auditoria`);
    } catch (error) {
      toast.error(extractApiError(error, 'Erro ao abrir SACPM'));
    } finally {
      setCreatingSacpm(false);
    }
  };

  const handleDeleteChecklistItem = async (itemId) => {
    try {
      await auditsApi.deleteChecklistItem(itemId);
      setChecklistItems(prev => prev.filter(i => i.id !== itemId));
    } catch { toast.error('Erro ao excluir'); }
  };

  const handleSaveReport = async () => {
    setSavingReport(true);
    try {
      await auditsApi.updateReport(selectedAudit.id, reportForm);
      toast.success('Relatório salvo');
      setSelectedAudit(prev => ({ ...prev, ...reportForm }));
    } catch { toast.error('Erro ao salvar relatório'); } finally { setSavingReport(false); }
  };

  const filteredAudits = audits.filter(a =>
    a.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.areas?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StatusBadge = ({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.planned;
    const Icon = config.icon;
    return (
      <span className={`badge ${config.color}`}>
        <Icon size={12} className="mr-1" />
        {config.label}
      </span>
    );
  };

  const ResultBadge = ({ result }) => {
    if (!result) return <span className="badge badge-gray">-</span>;
    const config = RESULT_CONFIG[result] || RESULT_CONFIG.pending;
    return <span className={`badge ${config.color}`}>{config.label}</span>;
  };

  const COLUMNS = [
    { key: 'code', label: 'Código', locked: true, render: (audit) => <td className="font-mono text-green-400">{audit.code}</td> },
    { key: 'title', label: 'Título', render: (audit) => <td><div className="max-w-xs truncate">{audit.title}</div></td> },
    { key: 'type', label: 'Tipo', render: (audit) => <td>{AUDIT_TYPES.find(t => t.value === audit.audit_type)?.label || audit.audit_type}</td> },
    { key: 'status', label: 'Status', render: (audit) => <td><StatusBadge status={audit.status} /></td> },
    { key: 'result', label: 'Resultado', render: (audit) => <td><ResultBadge result={audit.result} /></td> },
    { key: 'date', label: 'Data', render: (audit) => <td>{audit.planned_date ? new Date(audit.planned_date).toLocaleDateString('pt-BR') : '-'}</td> },
    { key: 'findings', label: 'Constatações', render: (audit) => <td className="text-center">{audit.total_findings || 0}</td> },
    { key: 'actions', label: 'Ações', locked: true, headerClass: 'text-right', render: (audit) => (
      <td className="text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => handleViewDetails(audit)}
            className="p-1 hover:text-blue-400 transition-colors"
            title="Ver detalhes"
          >
            <Eye size={16} />
          </button>
          {audit.audit_type === 'internal' && (
          <button
            onClick={() => navigate(`/auditorias/relatorio/${audit.id}`)}
            className="p-1 hover:text-green-400 transition-colors"
            title="Relatório de Auditoria"
          >
            <FileText size={16} />
          </button>
          )}
          {audit.audit_type === 'internal' && (
          <button
            onClick={() => navigate(`/auditorias/agenda/${audit.id}`)}
            className="p-1 hover:text-teal-400 transition-colors"
            title="Agenda Auditoria Interna"
          >
            <CheckSquare size={16} />
          </button>
          )}
          {audit.audit_type !== 'internal' && (
            <button
              onClick={() => handleViewDetails(audit)}
              className="p-1 hover:text-teal-400 transition-colors"
              title="Documentos"
            >
              <FileText size={16} />
            </button>
          )}
          {audit.status !== 'completed' && audit.status !== 'cancelled' && (
            <button
              onClick={() => handleFinalize(audit)}
              className="p-1 hover:text-yellow-400 transition-colors"
              title="Finalizar auditoria"
            >
              <CheckCircle size={16} />
            </button>
          )}
          {audit.status !== 'completed' && (
            <button
              onClick={() => handleOpenModal(audit)}
              className="p-1 hover:text-green-400 transition-colors"
              title="Editar"
            >
              <Edit size={16} />
            </button>
          )}
          <button
            onClick={() => handleDelete(audit)}
            className="p-1 hover:text-red-400 transition-colors"
            title="Excluir"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    ) },
  ];

  const { visibleColumns, visibleKeys, toggleColumn, resetColumns } = useColumnVisibility({ storageKey: 'audits', columns: COLUMNS });

  return (
    <div className="space-y-6" data-testid="audits-page">
      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-700 mb-2">
        {[
          { id: 'audits', label: 'Auditorias', icon: FileText },
          { id: 'items', label: 'Itens de Auditoria', icon: ClipboardCheck },
          { id: 'auditors', label: 'Auditores', icon: UserCheck },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
              activeTab === tab.id ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'items' ? (
        <AuditItems />
      ) : activeTab === 'auditors' ? (
        <Auditors />
      ) : (
      <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Gestão de Auditorias</h1>
          <p className="text-gray-400 text-sm">Planeje e execute auditorias internas e externas</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/auditorias/agenda-geral')}
            className="btn-secondary flex items-center gap-2"
            data-testid="agenda-geral-btn"
          >
            <Calendar size={20} />
            Agenda Geral
          </button>
          <ExportButtons
            module="audits"
            filters={{
              ...(filterType && { audit_type: filterType }),
              ...(filterStatus && { status: filterStatus }),
              ...(searchTerm && { search: searchTerm }),
            }}
          />
          <ColumnSelector columns={COLUMNS} visibleKeys={visibleKeys} onToggle={toggleColumn} onReset={resetColumns} />
          <button
            onClick={() => handleOpenModal()}
            className="btn-primary"
            data-testid="new-audit-btn"
          >
            <Plus size={20} />
            Nova Auditoria
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Total do Ano</p>
            <p className="text-2xl font-bold text-white">{stats.total_year}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Concluídas</p>
            <p className="text-2xl font-bold text-green-500">{stats.completed}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Planejadas</p>
            <p className="text-2xl font-bold text-blue-500">{stats.planned}</p>
          </div>
          <div className="card p-4">
            <p className="text-gray-400 text-sm">Aprovadas</p>
            <p className="text-2xl font-bold text-green-500">{stats.by_result?.approved || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Buscar por código, título ou áreas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10 w-full"
              data-testid="search-input"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input px-3 w-full sm:w-60"
            data-testid="type-filter"
          >
            <option value="">Todos os Tipos</option>
            {AUDIT_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input px-3 w-full sm:w-60"
            data-testid="status-filter"
          >
            <option value="">Todos os Status</option>
            {Object.entries(STATUS_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
          <button onClick={loadData} className="btn-secondary" data-testid="refresh-btn">
            <RefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin text-green-500" size={32} />
          </div>
        ) : filteredAudits.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            Nenhuma auditoria encontrada
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={col.headerClass || ''}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAudits.map((audit) => (
                  <tr key={audit.id} data-testid={`audit-row-${audit.id}`}>
                    {visibleColumns.map(col => (
                      <Fragment key={col.key}>{col.render(audit)}</Fragment>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {editingAudit ? 'Editar Auditoria' : 'Nova Auditoria'}
              </h2>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Título *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="input w-full"
                  placeholder="Título da auditoria"
                  data-testid="audit-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Descrição</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Descrição geral da auditoria"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Tipo</label>
                  <select
                    value={formData.audit_type}
                    onChange={(e) => setFormData({...formData, audit_type: e.target.value})}
                    className="input w-full"
                  >
                    {AUDIT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Data Planejada</label>
                  <input
                    type="date"
                    value={formData.planned_date}
                    onChange={(e) => setFormData({...formData, planned_date: e.target.value})}
                    className="input w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Data Início</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="input w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Data Fim</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="input w-full"
                    min="1900-01-01"
                    max="2099-12-31"
                  />
                </div>
              </div>

              {formData.audit_type !== 'internal' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Anexar Documentos <span className="text-gray-500">(opcional)</span>
                  </label>
                  <input
                    type="file"
                    multiple
                    onChange={handleAddPendingFiles}
                    className="input w-full"
                  />
                  {pendingDocuments.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {pendingDocuments.map((d, idx) => (
                        <li key={idx} className="flex items-center justify-between bg-gray-800 rounded px-3 py-1.5 text-sm">
                          <span className="text-gray-300 truncate">{d.file.name}</span>
                          <button type="button" onClick={() => handleRemovePendingFile(idx)} className="text-red-400 hover:text-red-300 ml-2">
                            <X size={14} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Escopo</label>
                <textarea
                  value={formData.scope}
                  onChange={(e) => setFormData({...formData, scope: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Defina o escopo da auditoria"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Objetivos</label>
                <textarea
                  value={formData.objectives}
                  onChange={(e) => setFormData({...formData, objectives: e.target.value})}
                  className="input w-full"
                  rows={2}
                  placeholder="Liste os objetivos da auditoria"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Critérios (Normas/Procedimentos)</label>
                <input
                  type="text"
                  value={formData.criteria}
                  onChange={(e) => setFormData({...formData, criteria: e.target.value})}
                  className="input w-full"
                  placeholder="Ex: ISO 9001:2015, POP-001..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Áreas/Processos Auditados</label>
                <input
                  type="text"
                  value={formData.areas}
                  onChange={(e) => setFormData({...formData, areas: e.target.value})}
                  className="input w-full"
                  placeholder="Ex: Produção, Qualidade, RH..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Equipe de Auditores</label>
                {auditors.length === 0 ? (
                  <p className="text-xs text-gray-500">
                    Nenhum auditor cadastrado. Cadastre em <span className="text-green-400">Cadastros &gt; Auditores</span>.
                  </p>
                ) : (
                  <div className="flex gap-2 flex-wrap">
                    {auditors.map((a) => {
                      const selected = formData.selected_auditor_ids.includes(a.id);
                      return (
                        <label
                          key={a.id}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                            selected
                              ? 'border-blue-500 bg-blue-500/20 text-blue-200'
                              : 'border-gray-700 text-gray-400 hover:border-gray-500'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => handleToggleAuditor(a.id)}
                            className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500"
                          />
                          {a.name}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {!editingAudit && formData.audit_type === 'internal' && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Certificações <span className="text-gray-500">(opcional — escolha para saber quais itens carregar)</span>
                  </label>
                  {certificates.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      Nenhuma certificação ativa. Cadastre em <span className="text-green-400">Cadastros &gt; Certificações</span>.
                    </p>
                  ) : (
                    <>
                      <div className="flex gap-2 flex-wrap">
                        {certificates.map((cert) => {
                          const selected = formData.certificate_ids.includes(cert.id);
                          return (
                            <label
                              key={cert.id}
                              className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer transition-colors ${
                                selected
                                  ? 'border-purple-500 bg-purple-500/20 text-purple-200'
                                  : 'border-gray-700 text-gray-400 hover:border-gray-500'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={selected}
                                onChange={() => handleToggleCertificate(cert.id)}
                                className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-purple-500"
                              />
                              {cert.name}
                            </label>
                          );
                        })}
                      </div>
                      {certItemCount !== null && (
                        <p className="text-xs text-gray-400 mt-2">
                          <span className="text-purple-400">{certItemCount}</span> item(ns) serão carregados na lista de verificação a partir das certificações selecionadas.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  E-mail para envio da auditoria <span className="text-gray-500">(opcional)</span>
                </label>
                <input
                  type="email"
                  value={formData.share_email}
                  onChange={(e) => setFormData({...formData, share_email: e.target.value})}
                  className="input w-full"
                  placeholder="exemplo@empresa.com"
                  title="Se informado, um link de acesso à auditoria será enviado por e-mail"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Se preenchido, um link público de acesso à auditoria será enviado ao destinatário.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={handleCloseModal} className="btn-secondary">
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={saving} data-testid="audit-submit-btn">
                  {saving ? <Loader2 className="animate-spin" size={20} /> : editingAudit ? 'Salvar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedAudit && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-3xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-white">{selectedAudit.code}</h2>
                <p className="text-gray-400">{selectedAudit.title}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            {/* Detail Modal Tabs */}
            <div className="flex gap-1 border-b border-gray-700 mb-4">
              {[
                { id: 'info', label: 'Informações' },
                ...(selectedAudit.audit_type === 'internal'
                  ? [{ id: 'checklist', label: 'Lista de Verificação' }]
                  : [{ id: 'documents', label: 'Documentos' }]),
                { id: 'report', label: 'Relatório' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setDetailTab(tab.id);
                    if (tab.id === 'checklist' && checklistItems.length === 0) loadChecklistItems(selectedAudit.id);
                  }}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    detailTab === tab.id ? 'border-green-500 text-green-400' : 'border-transparent text-gray-400 hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            
            <div className="space-y-4 overflow-y-auto max-h-[60vh]">
              {/* Tab: Informações */}
              {detailTab === 'info' && (
                <>
                  <div className="flex gap-2 flex-wrap">
                    <StatusBadge status={selectedAudit.status} />
                    <ResultBadge result={selectedAudit.result} />
                    <span className="badge badge-gray">
                      {AUDIT_TYPES.find(t => t.value === selectedAudit.audit_type)?.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-400 text-sm">Data Planejada</p>
                      <p className="text-white">{selectedAudit.planned_date ? new Date(selectedAudit.planned_date).toLocaleDateString('pt-BR') : '-'}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Período Execução</p>
                      <p className="text-white">
                        {selectedAudit.start_date && selectedAudit.end_date
                          ? `${new Date(selectedAudit.start_date).toLocaleDateString('pt-BR')} a ${new Date(selectedAudit.end_date).toLocaleDateString('pt-BR')}`
                          : '-'}
                      </p>
                    </div>
                  </div>
                  {selectedAudit.scope && <div><p className="text-gray-400 text-sm">Escopo</p><p className="text-white whitespace-pre-wrap">{selectedAudit.scope}</p></div>}
                  {selectedAudit.objectives && <div><p className="text-gray-400 text-sm">Objetivos</p><p className="text-white whitespace-pre-wrap">{selectedAudit.objectives}</p></div>}
                  {selectedAudit.criteria && <div><p className="text-gray-400 text-sm">Critérios</p><p className="text-white">{selectedAudit.criteria}</p></div>}
                  {selectedAudit.areas && <div><p className="text-gray-400 text-sm">Áreas Auditadas</p><p className="text-white">{selectedAudit.areas}</p></div>}
                  <div className="bg-gray-800 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2"><ClipboardCheck size={20} />Resumo das Constatações</h3>
                    <div className="grid grid-cols-5 gap-4 text-center">
                      <div><p className="text-2xl font-bold text-green-500">{selectedAudit.conformities || 0}</p><p className="text-gray-400 text-xs">Conformidades</p></div>
                      <div><p className="text-2xl font-bold text-yellow-500">{selectedAudit.minor_ncs || 0}</p><p className="text-gray-400 text-xs">NC Menores</p></div>
                      <div><p className="text-2xl font-bold text-red-500">{selectedAudit.major_ncs || 0}</p><p className="text-gray-400 text-xs">NC Maiores</p></div>
                      <div><p className="text-2xl font-bold text-blue-500">{selectedAudit.observations || 0}</p><p className="text-gray-400 text-xs">Observações</p></div>
                      <div><p className="text-2xl font-bold text-purple-500">{selectedAudit.opportunities || 0}</p><p className="text-gray-400 text-xs">Oportunidades</p></div>
                    </div>
                  </div>
                </>
              )}

              {/* Tab: Lista de Verificação */}
              {detailTab === 'checklist' && (
                <div className="space-y-3">
                  <div className="bg-gray-800 rounded-lg p-3">
                    <p className="text-xs text-gray-400 mb-2 font-medium">Adicionar item</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <input className="input" placeholder="Requisito/Cláusula" value={newChecklistItem.requirement} onChange={e => setNewChecklistItem({ ...newChecklistItem, requirement: e.target.value })} />
                      <input className="input md:col-span-2" placeholder="Pergunta / Item de verificação *" value={newChecklistItem.question} onChange={e => setNewChecklistItem({ ...newChecklistItem, question: e.target.value })} />
                      <input className="input" placeholder="Área" value={newChecklistItem.area} onChange={e => setNewChecklistItem({ ...newChecklistItem, area: e.target.value })} />
                      <button onClick={handleAddChecklistItem} className="btn-primary col-span-1 md:col-span-2 flex items-center justify-center gap-1">
                        <Plus size={14} />Adicionar
                      </button>
                    </div>
                  </div>
                  {loadingChecklist ? (
                    <div className="flex justify-center py-6"><Loader2 className="animate-spin text-green-500" size={24} /></div>
                  ) : checklistItems.length === 0 ? (
                    <p className="text-gray-500 text-center py-6">Nenhum item cadastrado. Adicione acima.</p>
                  ) : (
                    <div className="space-y-2">
                      {checklistItems.map((item, idx) => (
                        <div key={item.id} className="bg-gray-800 rounded p-3 flex items-start gap-3">
                          <span className="text-gray-500 text-xs w-5 shrink-0 mt-1">{idx + 1}</span>
                          <div className="flex-1 min-w-0">
                            {item.requirement && <p className="text-xs text-gray-400">{item.requirement}</p>}
                            <p className="text-white text-sm">{item.question}</p>
                            {item.area && <p className="text-xs text-gray-500">{item.area}</p>}
                          </div>
                          <div className="shrink-0 flex items-center gap-2">
                            <select
                              value={item.result || ''}
                              onChange={(e) => handleUpdateChecklistResult(item.id, e.target.value)}
                              className={`input w-40 ${CHECKLIST_RESULTS.find(r => r.value === item.result)?.active || ''}`}
                            >
                              <option value="">Selecionar...</option>
                              {CHECKLIST_RESULTS.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                            <button onClick={() => handleDeleteChecklistItem(item.id)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Documentos */}
              {detailTab === 'documents' && (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <input type="file" onChange={handleUploadDocument} className="input flex-1" data-testid="document-upload-input" />
                  </div>
                  {loadingDocuments ? (
                    <div className="flex justify-center py-6"><Loader2 className="animate-spin text-green-500" size={24} /></div>
                  ) : documents.length === 0 ? (
                    <p className="text-gray-500 text-center py-6">Nenhum documento anexado.</p>
                  ) : (
                    <div className="space-y-2">
                      {documents.map((doc) => (
                        <div key={doc.id} className="bg-gray-800 rounded p-3 flex items-center gap-3">
                          <FileText size={16} className="text-blue-400 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm truncate">{doc.file_name}</p>
                            <p className="text-xs text-gray-400">
                              {(doc.file_size / 1024).toFixed(1)} KB · {new Date(doc.created_at).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          <button onClick={() => handleDownloadDocument(doc)} className="p-1 hover:text-blue-400 transition-colors" title="Baixar">
                            <Download size={16} />
                          </button>
                          <button onClick={() => handleDeleteDocument(doc)} className="p-1 hover:text-red-400 transition-colors" title="Excluir">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Relatório */}
              {detailTab === 'report' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Resumo Executivo</label>
                    <textarea className="input w-full" rows={4} value={reportForm.summary} onChange={e => setReportForm({ ...reportForm, summary: e.target.value })} placeholder="Resumo geral da auditoria..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Conclusões</label>
                    <textarea className="input w-full" rows={4} value={reportForm.conclusions} onChange={e => setReportForm({ ...reportForm, conclusions: e.target.value })} placeholder="Conclusões e parecer final..." />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Recomendações</label>
                    <textarea className="input w-full" rows={4} value={reportForm.recommendations} onChange={e => setReportForm({ ...reportForm, recommendations: e.target.value })} placeholder="Recomendações de melhoria..." />
                  </div>
                  <button onClick={handleSaveReport} disabled={savingReport} className="btn-primary flex items-center gap-2">
                    {savingReport ? <Loader2 size={16} className="animate-spin" /> : null}
                    Salvar Relatório
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700 mt-4">
              <button onClick={() => setShowDetailModal(false)} className="btn-secondary">Fechar</button>
              <button onClick={() => { setShowDetailModal(false); handleOpenModal(selectedAudit); }} className="btn-primary flex items-center gap-2">
                <Edit size={16} />Editar
              </button>
            </div>
          </div>
        </>
      )}

      {/* SACPM confirmation modal */}
      {pendingNcItem && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-content p-6 max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold flex items-center gap-2"><AlertTriangle size={18} className="text-red-400" />Abrir SACPM?</h3>
              <button onClick={() => !creatingSacpm && setPendingNcItem(null)} className="text-gray-400 hover:text-white"><X size={20} /></button>
            </div>
            <p className="text-gray-300 text-sm">
              O item da auditoria foi marcado como não conforme. Deseja abrir um SACPM do tipo <strong className="text-white">Ação Corretiva</strong> com origem <strong className="text-white">Auditoria</strong>?
            </p>
            <div className="mt-3 bg-gray-800 rounded p-3 text-sm">
              {pendingNcItem.requirement && <p className="text-xs text-gray-400">{pendingNcItem.requirement}</p>}
              <p className="text-white">{pendingNcItem.question}</p>
              {pendingNcItem.area && <p className="text-xs text-gray-500">{pendingNcItem.area}</p>}
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setPendingNcItem(null)} disabled={creatingSacpm} className="btn-secondary">Não</button>
              <button onClick={() => handleCreateSacpmFromAudit(true)} disabled={creatingSacpm} className="btn-primary flex items-center gap-2">
                {creatingSacpm && <Loader2 size={16} className="animate-spin" />}
                Abrir SACPM
              </button>
            </div>
          </div>
        </>
      )}
      </>
      )}
    </div>
  );
};

export default Audits;
